import React from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { Link } from '@tanstack/react-router';
import {
  Card,
  CardHeader,
  CardContent,
  Flex,
  Heading,
  Text,
  Button,
  Select,
  DataTable,
  LoadingOrbit,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  TextField,
  TagBadge,
  type SelectOption,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import { ArrowSquareOut } from '@phosphor-icons/react';
import {
  useIssueReviewsQuery,
  useResolveReviewMutation,
  type IssueReviewListItem,
  type IssueReviewStatus,
} from '../../../hooks/useIssueReviewQueue';
import { useOrganizationsQuery } from './useSuperadminData';

const STATUS_OPTIONS: SelectOption[] = [
  { id: 'all', label: 'All statuses' },
  { id: 'pending', label: 'Pending' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'dismissed', label: 'Dismissed' },
];

const STATUS_VARIANTS: Record<IssueReviewStatus, 'warning' | 'success' | 'secondary'> = {
  pending: 'warning',
  resolved: 'success',
  dismissed: 'secondary',
};

const SEVERITY_VARIANTS: Record<string, 'destructive' | 'warning' | 'outline' | 'secondary'> = {
  high: 'destructive',
  medium: 'warning',
  low: 'outline',
  unknown: 'secondary',
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
};

function ResolveReviewContent({
  review,
  onClose,
}: {
  review: IssueReviewListItem;
  onClose: () => void;
}) {
  const [resolutionNote, setResolutionNote] = React.useState('');
  const resolveMutation = useResolveReviewMutation();

  const handleResolve = async (status: 'resolved' | 'dismissed') => {
    await resolveMutation.mutateAsync({
      id: review.id,
      status,
      resolutionNote: resolutionNote.trim() || undefined,
    });
    onClose();
  };

  return (
    <>
      <DialogContent className="max-h-[70vh] overflow-y-auto">
        <Flex direction="column" gap="lg">
          {/* Issue Info */}
          <Flex direction="column" gap="sm">
            <Flex gap="md" align="center">
              <TagBadge
                name={review.issue.severity}
                color={badgeVariantToTagColor(
                  SEVERITY_VARIANTS[review.issue.severity] ?? 'secondary'
                )}
                hideDot
              />
              <TagBadge name={review.issue.categoryKey} color="gray" hideDot />
            </Flex>
            <Text weight="medium">{review.issue.title}</Text>
          </Flex>

          {/* Organization */}
          <Flex direction="column" gap="xs">
            <Text size="xs" className="text-muted-foreground uppercase tracking-wide">
              Organization
            </Text>
            <Text size="sm">{review.organization.name ?? 'Unknown'}</Text>
          </Flex>

          {/* Flag Reason */}
          <Flex direction="column" gap="xs">
            <Text size="xs" className="text-muted-foreground uppercase tracking-wide">
              Flag Reason
            </Text>
            <Text size="sm" className="whitespace-pre-wrap">
              {review.reason}
            </Text>
          </Flex>

          {/* Flagged By */}
          <Flex direction="column" gap="xs">
            <Text size="xs" className="text-muted-foreground uppercase tracking-wide">
              Flagged By
            </Text>
            <Text size="sm">
              {review.flaggedBy?.displayName ?? review.flaggedBy?.primaryEmail ?? 'Unknown'}
            </Text>
            <Text size="xs" className="text-muted-foreground">
              {formatDateTime(review.flaggedAt)}
            </Text>
          </Flex>

          {/* Resolution Note */}
          {review.status === 'pending' && (
            <TextField
              label="Resolution Note (optional)"
              placeholder="Add any notes about how this was resolved..."
              value={resolutionNote}
              onChange={(value) => setResolutionNote(String(value))}
            />
          )}

          {/* Existing Resolution */}
          {review.status !== 'pending' && review.resolutionNote && (
            <Flex direction="column" gap="xs">
              <Text size="xs" className="text-muted-foreground uppercase tracking-wide">
                Resolution Note
              </Text>
              <Text size="sm" className="whitespace-pre-wrap">
                {review.resolutionNote}
              </Text>
            </Flex>
          )}
        </Flex>
      </DialogContent>
      <DialogFooter>
        <Flex justify="between" className="w-full">
          <Link
            to="/issues"
            search={{ issueId: review.issue.id }}
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
          >
            <ArrowSquareOut size={16} />
            View Issue
          </Link>
          {review.status === 'pending' ? (
            <Flex gap="sm">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="secondary"
                isDisabled={resolveMutation.isPending}
                onClick={() => handleResolve('dismissed')}
              >
                Dismiss
              </Button>
              <Button
                variant="primary"
                isDisabled={resolveMutation.isPending}
                onClick={() => handleResolve('resolved')}
              >
                Mark Resolved
              </Button>
            </Flex>
          ) : (
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          )}
        </Flex>
      </DialogFooter>
    </>
  );
}

export function SuperadminIssueReviews() {
  const [statusFilter, setStatusFilter] = React.useState<SelectOption['id']>('pending');
  const [orgFilter, setOrgFilter] = React.useState<SelectOption['id']>('all');
  const [page, setPage] = React.useState(1);
  const [selectedReview, setSelectedReview] = React.useState<IssueReviewListItem | null>(null);
  const pageSize = 20;

  const organizationsQuery = useOrganizationsQuery();

  const orgOptions = React.useMemo<SelectOption[]>(() => {
    const orgs =
      organizationsQuery.data?.organizations?.map((org) => ({
        id: org.id,
        label: org.name,
      })) ?? [];
    return [{ id: 'all', label: 'All organizations' }, ...orgs];
  }, [organizationsQuery.data?.organizations]);

  React.useEffect(() => {
    setPage(1);
  }, [statusFilter, orgFilter]);

  const reviewsQuery = useIssueReviewsQuery({
    status: statusFilter === 'all' ? undefined : (statusFilter as IssueReviewStatus),
    orgId: orgFilter === 'all' ? undefined : String(orgFilter),
    page,
    pageSize,
  });

  const columns = React.useMemo<ColumnDef<StockFeatures, IssueReviewListItem>[]>(() => {
    return [
      {
        header: 'Organization',
        accessorKey: 'organization',
        cell: ({ row }) => (
          <Text weight="medium">{row.original.organization.name ?? 'Unknown'}</Text>
        ),
      },
      {
        header: 'Issue',
        accessorKey: 'issue',
        cell: ({ row }) => (
          <Flex direction="column" gap="xs">
            <Text weight="medium" className="line-clamp-2">
              {row.original.issue.title}
            </Text>
            <Flex gap="xs">
              <TagBadge
                name={row.original.issue.severity}
                color={badgeVariantToTagColor(
                  SEVERITY_VARIANTS[row.original.issue.severity] ?? 'secondary'
                )}
                hideDot
                size="sm"
              />
              <TagBadge name={row.original.issue.categoryKey} color="gray" hideDot size="sm" />
            </Flex>
          </Flex>
        ),
      },
      {
        header: 'Reason',
        accessorKey: 'reason',
        cell: ({ row }) => (
          <Text size="sm" className="line-clamp-2 text-muted-foreground">
            {row.original.reason}
          </Text>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => (
          <TagBadge
            name={row.original.status}
            color={badgeVariantToTagColor(STATUS_VARIANTS[row.original.status])}
            hideDot
          />
        ),
      },
      {
        header: 'Flagged',
        accessorKey: 'flaggedAt',
        cell: ({ row }) => (
          <Flex direction="column">
            <Text size="sm" className="text-muted-foreground">
              {formatDateTime(row.original.flaggedAt)}
            </Text>
            {row.original.flaggedBy && (
              <Text size="xs" className="text-muted-foreground">
                by {row.original.flaggedBy.displayName ?? row.original.flaggedBy.primaryEmail}
              </Text>
            )}
          </Flex>
        ),
      },
      {
        header: '',
        id: 'actions',
        cell: ({ row }) => (
          <Link
            to="/issues"
            search={{ issueId: row.original.issue.id }}
            className="inline-flex items-center justify-center p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="View issue"
            onClick={(e) => e.stopPropagation()}
          >
            <ArrowSquareOut size={18} />
          </Link>
        ),
      },
    ];
  }, []);

  const pagination = reviewsQuery.data?.pagination;
  const canPrevious = page > 1;
  const canNext = pagination ? page < (pagination.totalPages || 0) : false;

  return (
    <>
      <Card padding="lg">
        <CardHeader>
          <Flex align="center" justify="between" wrap gap="md">
            <div>
              <Heading level={3}>Flagged Issues</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                Issues flagged for superadmin review across all organizations.
              </Text>
            </div>
          </Flex>
        </CardHeader>
        <CardContent>
          <Flex gap="md" wrap className="mb-4">
            <Select
              label="Status"
              items={STATUS_OPTIONS}
              selectedKey={statusFilter}
              onSelectionChange={(key) => setStatusFilter(String(key))}
              className="min-w-[200px]"
            />
            <Select
              label="Organization"
              items={orgOptions}
              selectedKey={orgFilter}
              onSelectionChange={(key) => setOrgFilter(String(key))}
              isDisabled={organizationsQuery.isLoading}
              className="min-w-[200px]"
            />
          </Flex>

          {reviewsQuery.isLoading ? (
            <Flex align="center" justify="center" className="py-12">
              <LoadingOrbit size="lg" />
            </Flex>
          ) : reviewsQuery.isError ? (
            <Text className="text-destructive">
              {reviewsQuery.error instanceof Error
                ? reviewsQuery.error.message
                : 'Failed to load flagged issues'}
            </Text>
          ) : reviewsQuery.data && reviewsQuery.data.data.length > 0 ? (
            <DataTable
              columns={columns}
              data={reviewsQuery.data.data}
              onRowClick={(row) => setSelectedReview(row)}
              initialSorting={[{ id: 'flaggedAt', desc: true }]}
            />
          ) : (
            <Text className="text-muted-foreground py-6">No flagged issues found.</Text>
          )}

          <Flex align="center" justify="between" className="mt-4">
            <Text size="sm" className="text-muted-foreground">
              Page {pagination?.page ?? 1} of {pagination?.totalPages ?? 1} (
              {pagination?.totalItems ?? 0} total)
            </Text>
            <Flex gap="sm">
              <Button
                size="sm"
                variant="outline"
                isDisabled={!canPrevious || reviewsQuery.isFetching}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                isDisabled={!canNext || reviewsQuery.isFetching}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Next
              </Button>
            </Flex>
          </Flex>
        </CardContent>
      </Card>

      {selectedReview && (
        <DialogTrigger
          isOpen
          onOpenChange={(isOpen) => !isOpen && setSelectedReview(null)}
          size="lg"
        >
          <span />
          <Dialog title="Review Flagged Issue">
            <ResolveReviewContent review={selectedReview} onClose={() => setSelectedReview(null)} />
          </Dialog>
        </DialogTrigger>
      )}
    </>
  );
}
